import {KTSVG} from '../../../../_metronic/helpers'
import React, {useEffect, useState} from 'react'
import {
  createClientDemandeMessage,
  getClientDemandeMessageByDemandeId,
} from '../../../../components/requests'
import {useFormik} from 'formik'
import {ModeleClientDemandeMessage} from '../../../../components/Models'

interface FormValues {
  message: string
}

// Validation du formulaire de message
const validate = (value) => {
  const error: Partial<FormValues> = {}

  if (!value.message) {
    // Si le message est null
    error.message = 'Le message est requis'
  } else if (value.message.trim().length === 0) {
    // Si le message est une chaine de caractère vide
    error.message = 'Veuillez écrire un message valide'
  }

  return error
}

interface ClientDemandeMessageModalProps {
  demandeId: any
  clientId?: any
  readOnly?: any
}

const ClientDemandeMessageModal: React.FC<ClientDemandeMessageModalProps> = (
  {
    demandeId,
    readOnly = false,
    clientId,
  }) => {
  const [spinner, setSpinner] = useState<boolean>(false)
  const [messages, setMessages] = useState<Array<ModeleClientDemandeMessage>>([])

  // Retourne un tableau de message sur la demande concernée
  const getClientDemandeMessage = (signal) => {
    getClientDemandeMessageByDemandeId(demandeId, signal)
      .then((response) => {
        if (response?.status === 200 && response?.data !== null) {
          if (demandeId) {
            setMessages(response?.data?.reverse())
          }
        }
      })
      .catch(() => {
        console.log('error')
      })
  }

  const formik = useFormik({
    initialValues: {
      message: '',
    },
    validate,
    onSubmit: (value) => {
      submitMessage(demandeId, value.message.trim(), clientId)
    },
  })

  const submitMessage = (demandeId, message, clientId) => {
    setSpinner(true)
    // Création du toast de loading sur le container qui a l'Id "messageToastContainerId"
    createClientDemandeMessage(demandeId, message, clientId)
      .then((response) => {
        if (response?.data !== null && response?.status === 200) {
          // Si la requête est OK,
          // Ajout du message ajouté dans le tableau de message initial
          setMessages((prevMessages) => {
            return [
              {
                ...response?.data,
                client:
                  response?.data?.demande?.client?.nom +
                  ' ' +
                  response?.data?.demande?.client?.prenom,
              },
              ...prevMessages,
            ]
          })
          // Desactiver le spinner
          setSpinner(false)
        }
      })
      .catch(() => {
        // Si il y a une erreur
        console.log('error')
      })
      .finally(() => {
        // Que la requête soit OK ou non,
        // Desactiver le spinner
        setSpinner(false)
        // Reinitialiser le formulaire
        formik.resetForm()
      })
  }

  // Chargement des messages sur la demande
  useEffect(() => {
    if (demandeId) {
      const controller = new AbortController()
      const signal = controller.signal
      getClientDemandeMessage(signal)
      return () => controller.abort()
    }
  }, [])

  const convertDate = (date) => {
    let dateConvert = ''
    if (date) {
      dateConvert = new Date(date).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    }
    return dateConvert
  }

  const getStatutBadge = (statut: string | undefined): string => {
    if (!statut) return "badge badge-secondary"

    switch (statut) {
      case "A faire":
        return "badge badge-secondary"
      case "En cours":
        return "badge badge-success"
      case "Réalisée":
        return "badge badge-primary"
      default:
        return "badge badge-secondary"
    }
  }

  const getDateMessage = (message: any) => {
    if (message?.dateRealisation) return message?.dateRealisation
    if (message?.dateLecture) return message?.dateLecture
    if (message?.dateCreation) return message?.dateCreation
  }

  const displayDateMessage = (message) => {
    let date = getDateMessage(message)
    return (date && (<div>Le : {convertDate(date)}</div>))
  }

  return (
    <div className='modal fade' tabIndex={-1} id='modal_client_demande_message'>
      <div className='modal-dialog modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              Vous souhaitez être recontacté, laisser nous votre message :
            </h5>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              data-bs-dismiss='modal'
              aria-label='Close'
            >
              <KTSVG
                path='/media/icons/duotune/arrows/arr061.svg'
                className='svg-icon svg-icon-2x'
              />
            </div>
          </div>

          <div className='modal-body'>
            {!readOnly && demandeId !== null && (
              <form onSubmit={formik.handleSubmit}>
                <div className='mb-4'>
                  <label className='fw-bold' htmlFor='message'>Commentaires :</label>
                  <textarea
                    id='message'
                    name='message'
                    className='form-control form-control-flush'
                    placeholder='Votre message...'
                    style={{height: '100px'}}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.message}
                  ></textarea>
                </div>
                <div className='text-end'>
                  <button
                    type='button'
                    className='btn btn-secondary h-40px me-2'
                    data-bs-dismiss='modal'
                    aria-label='Close'
                  >
                    Fermer
                  </button>
                  <button
                    type='submit'
                    className='btn btn-icon btn-primary w-45px h-40px'
                    disabled={spinner || !formik.values.message.trim()}
                    aria-label='Envoyer'
                  >
                    {spinner ? (
                      <span className='spinner-border spinner-border-sm align-middle'></span>
                    ) : (
                      <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-2'/>
                    )}
                  </button>
                </div>
              </form>
            )}

            <hr/>

            <div>
              <p className='fw-bold'>Historique :</p>
              <div className='card border rounded mb-5'>
                {messages.length > 0 ? (
                  <div className='overflow-auto p-4' style={{height: '400px'}}>
                    {messages.map((item, index) => {
                      return (
                        <div key={index} className='d-flex' style={{height: '65px'}}>
                          <div className='bg-light rounded px-3 py-2 mb-2 w-100'>
                            <div className='d-flex justify-content-between'>
                              <div>
                                <p className='text-muted mb-1' style={{fontSize: '0.75rem'}}>
                                  {item.client} du :{' '}
                                  <strong className='text-dark'>
                                    {item.dateCreation && convertDate(item.dateCreation)}
                                  </strong>
                                </p>
                                <div
                                  className='text-dark'
                                  style={{whiteSpace: 'pre-line', lineHeight: 1.6}}
                                >
                                  {item.message?.replace(/\n{3,}/g, '\n\n')}
                                </div>
                              </div>

                              <div className='d-flex align-items-center justify-content-center' style={{width: '110px'}}>
                                <div
                                  className='d-flex flex-column align-items-center justify-content-center text-muted mb-1'
                                  style={{fontSize: '0.75rem'}}
                                >
                                  <div
                                    className={`${getStatutBadge(item?.statut)} d-flex justify-content-center mb-1`}
                                    style={{width: '100px', height: '25px'}}
                                  >
                                    {item?.statut ?? 'A faire'}
                                  </div>
                                  {displayDateMessage(item)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : demandeId !== null ? (
                  <span className='text-muted d-grid justify-content-center align-items-center h-100 my-4'>
                    Aucun message pour le moment.
                  </span>
                ) : (
                  ''
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientDemandeMessageModal
